"use client";

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { clientLogger } from '@/lib/client-logger';
import { DEBUG_FLAGS } from '@/lib/debug-config';

interface LocationPoint {
  id: string;
  title: string;
  address?: string;
  latitude: number;
  longitude: number;
  radiusMiles?: number | null;
  hideAddress?: boolean;
}

interface CampaignOverviewMapProps {
  locations: LocationPoint[];
  className?: string;
  height?: string;
}

const MapComponent: React.FC<{
  locations: LocationPoint[];
  height: string;
}> = ({ locations, height }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [initFailed, setInitFailed] = useState(false);

  // Check google availability during render (derived state, not a side effect)
  const googleMapsAvailable = useMemo(
    () => typeof google !== 'undefined' && !!google.maps,
    []
  );

  useEffect(() => {
    clientLogger.debug("MapComponent useEffect triggered", {
      hasRef: !!ref.current,
      hasMap: !!map,
      locationsLength: locations.length,
      googleMapsAvailable,
    });

    if (ref.current && !map && locations.length > 0 && googleMapsAvailable) {
      try {
        // Calculate bounds that include radius circles
        const bounds = new google.maps.LatLngBounds();

        locations.forEach(location => {
          const center = { lat: location.latitude, lng: location.longitude };

          if (location.radiusMiles && location.radiusMiles > 0) {
            // For locations with radius, extend bounds to include the full circle
            const radiusInMeters = location.radiusMiles * 1609.34;

            // Calculate approximate bounds for the circle
            // Using a simple approximation: 1 degree latitude ≈ 111km
            const latOffset = (radiusInMeters / 111000);
            const lngOffset = (radiusInMeters / (111000 * Math.cos(location.latitude * Math.PI / 180)));

            bounds.extend({ lat: location.latitude + latOffset, lng: location.longitude + lngOffset });
            bounds.extend({ lat: location.latitude - latOffset, lng: location.longitude - lngOffset });
            bounds.extend({ lat: location.latitude + latOffset, lng: location.longitude - lngOffset });
            bounds.extend({ lat: location.latitude - latOffset, lng: location.longitude + lngOffset });
          } else {
            // For exact locations, just add the center point
            bounds.extend(center);
          }
        });

        const center = bounds.getCenter();
        if (!center) {
          return;
        }

        const newMap = new window.google.maps.Map(ref.current, {
          center: { lat: center.lat(), lng: center.lng() },
          zoom: 10,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        setMap(newMap);

        // Fit map to show all locations with padding
        newMap.fitBounds(bounds, 80);
      } catch (err) {
        clientLogger.error("Error initializing Google Map", {
          error: err instanceof Error ? err.message : "Unknown error"
        });
        setInitFailed(true);
      }
    }
  }, [ref, map, locations, googleMapsAvailable]);

  // Add markers and circles for each location
  useEffect(() => {
    if (map && locations.length > 0) {
      try {
        locations.forEach((location, index) => {
          const hasRadius = location.radiusMiles && location.radiusMiles > 0;

          // Only show marker/pin for exact locations (no radius)
          if (!hasRadius) {
            // Create marker for exact locations
            const marker = new google.maps.Marker({
              position: { lat: location.latitude, lng: location.longitude },
              map: map,
              title: location.title,
              label: {
                text: (index + 1).toString(),
                color: 'white',
                fontWeight: 'bold',
              },
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
                scale: 15,
              },
            });

            // Create info window for exact locations
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div class="p-2">
                  <h3 class="font-semibold text-sm">${location.title}</h3>
                  ${location.address && !location.hideAddress
                    ? `<p class="text-xs text-gray-600 mt-1">${location.address}</p>`
                    : ''
                  }
                </div>
              `,
            });

            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });
          } else {
            // For radius locations, only show the circle (no pin/marker)
            const circle = new google.maps.Circle({
              strokeColor: '#3B82F6',
              strokeOpacity: 0.6,
              strokeWeight: 2,
              fillColor: '#3B82F6',
              fillOpacity: 0.15,
              map: map,
              center: { lat: location.latitude, lng: location.longitude },
              radius: (location.radiusMiles ?? 0) * 1609.34, // Convert miles to meters
            });

            // Add click listener to circle to show info
            const infoWindow = new google.maps.InfoWindow({
              content: `
                <div class="p-2">
                  <h3 class="font-semibold text-sm">${location.title}</h3>
                  <p class="text-xs text-gray-600 mt-1">Within ${location.radiusMiles} mile${location.radiusMiles !== 1 ? 's' : ''} of this area</p>
                </div>
              `,
              position: { lat: location.latitude, lng: location.longitude },
            });

            circle.addListener('click', () => {
              infoWindow.open(map);
            });
          }
        });
      } catch (err) {
        clientLogger.error("Error adding map markers", {
          error: err instanceof Error ? err.message : "Unknown error"
        });
      }
    }
  }, [map, locations]);

  if (!googleMapsAvailable || initFailed) {
    return (
      <div className="w-full rounded-lg border bg-gray-50 flex items-center justify-center" style={{ height }}>
        <div className="text-center p-4">
          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-gray-500">Map unavailable</p>
        </div>
      </div>
    );
  }

  return <div ref={ref} className="w-full rounded-lg border" style={{ height }} />;
};

const CampaignOverviewMap: React.FC<CampaignOverviewMapProps> = ({
  locations,
  className = '',
  height = '400px'
}) => {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  
  clientLogger.debug("CampaignOverviewMap API Key check", {
    hasApiKey: !!apiKey,
    locationsCount: locations.length,
    firstLocation: locations[0]
  });
  
  if (!apiKey) {
    return (
      <div className={`p-4 border rounded-lg bg-yellow-50 ${className}`}>
        <p className="text-yellow-800 font-medium">
          Google Maps API key not configured
        </p>
        <p className="text-yellow-700 text-sm mt-1">
          Please add NEXT_PUBLIC_GOOGLE_MAPS_API_KEY to your .env.local file to enable maps.
        </p>
        <div className="mt-2 text-xs text-yellow-600">
          <p>Expected locations: {locations.length}</p>
          {locations.length > 0 && (
            <p>First location: {locations[0].title} ({locations[0].latitude}, {locations[0].longitude})</p>
          )}
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className={`p-8 border rounded-lg bg-gray-50 text-center ${className}`}>
        <p className="text-gray-600">No locations to display on the map.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Debug: Show that component is rendering */}
      {DEBUG_FLAGS.showMapDebugInfo && (
        <div className="mb-2 text-xs text-gray-500">
          🗺️ Map component loading... (API Key: {apiKey ? 'Set' : 'Missing'}, Locations: {locations.length})
        </div>
      )}

      <Wrapper 
        apiKey={apiKey} 
        libraries={['geometry', 'places']}
        render={(status) => {
          clientLogger.debug("Google Maps Wrapper Status", { status });
          
          if (status === 'LOADING') {
            return (
              <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading map...</p>
                </div>
              </div>
            );
          }
          
          if (status === 'FAILURE') {
            return (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Failed to load Google Maps</p>
                <p className="text-red-600 text-sm mt-1">
                  Please check your API key and internet connection.
                </p>
              </div>
            );
          }
          
          if (status === 'SUCCESS') {
            return <MapComponent locations={locations} height={height} />;
          }
          
          return (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">Map status: {status}</p>
            </div>
          );
        }}
      />
      
      {DEBUG_FLAGS.showMapDebugInfo && locations.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-sm mb-2">Hunt Locations ({locations.length})</h4>
          <div className="space-y-1">
            {locations.map((location, index) => (
              <div key={location.id} className="flex items-center text-xs text-gray-600">
                <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-500 text-white rounded-full text-xs font-bold mr-2">
                  {index + 1}
                </span>
                <span className="font-medium">{location.title}</span>
                {location.radiusMiles && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                    {location.radiusMiles} mile{location.radiusMiles !== 1 ? 's' : ''} radius
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignOverviewMap;
